import React, { useState, useContext } from 'react'

import { Modal, DatePicker, Checkbox, Switch, Collapse, Divider, Tag, Tooltip, Card } from 'antd'
import { connect, useSelector } from 'react-redux'

import { SchoolContext } from '../../Providers/SchoolProvider'
import { selectUser } from '../../reducers/UserReducer'
import { faUserGraduate, faCircleInfo, faCalendar } from '@fortawesome/pro-solid-svg-icons'
import { onError } from '../../utils/apiHelper'
import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import IconLabel from '../icons/IconLabel'
import { getDocument } from '../../utils/api/export'
import { exportStudentsHours } from '../../utils/api/internship'
import { isNonEmptyArray } from '../../utils'
import { InternshipStates } from '../../utils/constants'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import GenericStatesPellet from '../shared/StatesPellet/GenericStatesPellet'

import '../../assets/modal-hours-export.scss'

const { Panel } = Collapse

const ModalHours = ({ visible, onClose }) => {
  const { useWeightedHours, schoolSections } = useContext(SchoolContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [selectedSchoolSections, setSelectedSchoolSections] = useState([])
  const [useStrictDatesFilter, setUseStrictDatesFilter] = useState(false)
  const [selectedStates, setSelectedSStates] = useState([])
  const [activePanelKey, setActivePanelKey] = useState('sections')

  const internshipStates = Object.entries(InternshipStates).map(([label, value]) => ({ label, value }))

  const isAllCheckedStates = selectedStates.length === internshipStates.length
  const isAllCheckedSections = selectedSchoolSections.length === schoolSections.length

  const handleSubmit = () => {
    exportStudentsHours(
      user,
      { startDate, endDate, filters: { studentActiveSections: selectedSchoolSections, states: selectedStates }, language: user.language.toLowerCase(), useWeightedHours, useStrictDatesFilter }
    ).then(({ job }) => getDocument(
      job,
      user,
      () => onError(t('spreadsheet_download.error'))
    )).catch(error => {
      console.error(error.message)
      onError(t('internships_export.error'))
    })
  }

  const handleCheckAllSections = () => {
    if (isAllCheckedSections) {
      setSelectedSchoolSections([])
    } else {
      setSelectedSchoolSections(schoolSections.map(ss => ss.id))
    }
  }

  const handleCheckAllStates = () => {
    if (isAllCheckedStates) {
      setSelectedSStates([])
    } else {
      setSelectedSStates(internshipStates.map(state => state.value))
    }
  }

  return (
    <Modal
      width={800}
      className='form-modal-with-footer modal-hours-export'
      title={
        <IconLabel
          icon={faCalendar}
          text={t('school.option.export_hours')}
        />
      }
      visible={visible}
      cancelText={t('modal.cancel_button')}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={t('common.export')}
      okButtonProps={{
        disabled:
          !startDate ||
          !endDate ||
          startDate > endDate ||
          selectedSchoolSections.length === 0 ||
          selectedStates.length === 0
      }}
      destroyOnClose
    >
      <Card
        size='small'
        className='modal-hours-export-card'
      >
        <div className='modal-hours-export-date-row'>
          <div className='modal-hours-export-date-field'>
            <div className='modal-hours-export-date-label'>
              {t('Start date')}
            </div>
            <DatePicker
              className='modal-hours-export-date-picker'
              placeholder={t('Start date')}
              onChange={setStartDate}
              value={startDate}
            />
          </div>
          <div className='modal-hours-export-date-field'>
            <div className='modal-hours-export-date-label'>
              {t('End date')}
            </div>
            <DatePicker
              className='modal-hours-export-date-picker'
              placeholder={t('End date')}
              onChange={setEndDate}
              value={endDate}
            />
          </div>
        </div>
        <div className='modal-hours-export-strict-row'>
          <Tag
            color='blue'
            className='modal-hours-export-strict-tag'
          >
            <Switch
              size='small'
              checked={useStrictDatesFilter}
              onChange={setUseStrictDatesFilter}
            />
            <span className='modal-hours-export-strict-text'>{t('hour.export.use_strict_dates')}</span>
            <Tooltip title={t('hour.export.use_strict_dates_tooltip')}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                className='modal-hours-export-strict-icon'
              />
            </Tooltip>
          </Tag>
        </div>
      </Card>
      <Collapse
        accordion
        activeKey={activePanelKey}
        onChange={(key) => {
          setActivePanelKey(Array.isArray(key) ? key[0] : key)
        }}
        className='modal-hours-export-collapse'
      >
        <Panel
          key='sections'
          header={
            <b className='modal-hours-export-panel-title'>
              <IconLabel icon={faUserGraduate} text={t('students_to_select')} />
            </b>
          }
        >
          <div>
            {isNonEmptyArray(schoolSections) && (
              <Checkbox
                checked={isAllCheckedSections}
                className='modal-hours-export-select-all'
                onChange={handleCheckAllSections}
              >
                {t('common.select_all')}
              </Checkbox>
            )}
            <Divider className='modal-hours-export-divider' />
            <Checkbox.Group
              className='modal-hours-export-checkbox-grid'
              value={selectedSchoolSections}
              onChange={setSelectedSchoolSections}
            >
              {schoolSections.map((ss) => (
                <Checkbox value={ss.id} key={ss.id} className='modal-hours-export-checkbox-item'>
                  {`${ss.sectionToYear.sectionAbbreviation} (${ss.sectionToYear.schoolYearName})`}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </div>
        </Panel>
        <Panel
          key='states'
          header={
            <b className='modal-hours-export-panel-title'>
              <IconLabel icon={faCalendar} text={t('states_to_select')} />
            </b>
          }
        >
          <div>
            {isNonEmptyArray(internshipStates) && (
              <Checkbox
                checked={isAllCheckedStates}
                className='modal-hours-export-select-all'
                onChange={handleCheckAllStates}
              >
                {t('common.select_all')}
              </Checkbox>
            )}
            <Divider className='modal-hours-export-divider' />
            <Checkbox.Group
              className='modal-hours-export-checkbox-grid'
              value={selectedStates}
              onChange={setSelectedSStates}
            >
              {internshipStates.map((state) => (
                <Checkbox
                  value={state.value}
                  key={state.value}
                  className='modal-hours-export-checkbox-item modal-hours-export-checkbox-item-state'
                >
                  <span
                    className='modal-hours-export-state-label'
                  >
                    <GenericStatesPellet state={state.value} />
                    {t(state.label)}
                  </span>
                </Checkbox>
              ))}
            </Checkbox.Group>
          </div>
        </Panel>
      </Collapse>
    </Modal>
  )
}

export default connect()(ModalHours)
